/**
 * 简谱图片识别（OpenRouter）。
 *
 * 与 import.html 的智谱 VLM 是同一套转写规则，但这里是**整图一次识别**：
 * import.html 先做谱行检测再逐行放大（精度优先，适合大谱对照校对）；
 * 编曲练习库入口追求「上传 → 入库 → 打开」一步到位，整图直接转写，
 * 识别质量靠用户在编辑器里校对。两份 prompt 是刻意的拷贝，改规则要同步。
 *
 * ⚠️ **存储键与 import.html 分开**（`sparks-openrouter-key` / `sparks-openrouter-model`
 * 对 import.html 的 `sparks-nmn-import-*`）。原来两边共用同一套键，换供应商之后那是危险的：
 * 同一台机器上，库存在库的智谱 Key 会被当成 OpenRouter Key 发给 openrouter.ai，反之亦然
 * —— 两者都表现为"Key 无效"，很容易查错方向。分开之后互不干扰，各自记各自的。
 *
 * Key 只存本机 localStorage，不上传服务器；首次使用时弹 prompt 让用户填一次。
 */
const PROMPT = `这是一张完整的简谱图片。请把整张谱转写为文本简谱，输出遵守以下规则：
1. 第一行输出标题：Dt: 标题文字（图片里没有明确标题就写 Dt: 未知）
2. 第二行输出调号拍速：P: 1=X 4/4 qpm=速度（拍速图片里没有就省略 qpm）
3. 第三行单独输出 ===（三个等号）
4. 之后每个谱行输出一个段落：段落内第一行是以 N: 开头的音符行，下面紧跟以 Lc: 开头的歌词行（没有歌词就省略歌词行）
   - 八度：音符上方有点加 e（如 1e），下方有点加 d（如 6d）
   - 附点：音符后加 .（如 5.）
   - 增时线写作 -；减时线（数字下横线）成组的八分音符用括号括起，如 (35)
   - 小节线写作 |；反复记号写作 ||: 和 :||；终止线写作 |||
   - 休止符写作 0
   - 连音线/延音线：在被连接的最后一个音符后加 ~
5. 相邻两个段落之间用单独一行 ---（三个减号）分隔——没有这个分隔，不同谱行会被当成同一行的多个声部
6. 多段歌词（同一行谱配多行歌词）只输出第一段
7. 如果图片不是简谱或完全无法辨认，只输出 [FAIL]
直接输出转写结果，不要解释、不要用代码块包裹。`

export const VLM_KEY_STORAGE = 'sparks-openrouter-key'
export const VLM_MODEL_STORAGE = 'sparks-openrouter-model'
export const VLM_DEFAULT_MODEL = 'stealth/space-bunny-alpha'

const VLM_ENDPOINT = 'https://openrouter.ai/api/v1/chat/completions'

export function getVlmKey(): string {
	return localStorage.getItem(VLM_KEY_STORAGE) ?? ''
}

export function setVlmKey(key: string) {
	localStorage.setItem(VLM_KEY_STORAGE, key.trim())
}

export function getVlmModel(): string {
	return localStorage.getItem(VLM_MODEL_STORAGE) ?? VLM_DEFAULT_MODEL
}

/**
 * 识别一张简谱图片，返回 NMN 源码。
 * @throws 模型判为无法辨认（[FAIL]）或 API/网络错误时抛出，文案可直接给用户看
 */
export async function recognizeScoreImage(file: File): Promise<string> {
	const key = getVlmKey()
	if (!key) {
		const input = prompt('首次使用图片识别，请粘贴 OpenRouter API Key（将保存在本机浏览器）：')
		if (!input || !input.trim()) throw new Error('未提供 API Key，已取消识别')
		setVlmKey(input)
		return recognizeScoreImage(file)
	}
	const dataUrl = await toPngDataUrl(file)
	const resp = await fetch(VLM_ENDPOINT, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'Authorization': 'Bearer ' + key,
			// OpenRouter 用这两个头做来源归因，缺了不影响调用，但带上方便在后台按站点筛选
			'HTTP-Referer': location.origin,
			'X-Title': 'Sparks Notation',
		},
		body: JSON.stringify({
			model: getVlmModel(),
			temperature: 0.1,
			// ⚠️ 这个参数是能不能用的关键，去掉就会挂。
			// space-bunny-alpha 默认会把整页谱面当像素逐个"推理"下去，陷入无界循环：
			// 实测流式请求收到 439 个 reasoning 片段、0 个 content 片段，且始终没有
			// finish_reason。非流式则表现为挂 5 分钟后返回一个纯空白 body（HTTP 200、
			// 非空白字符 0 个），前端 resp.json() 直接抛 SyntaxError。
			// 限成 'low' 之后同一张图 19.7 秒返回、705 字符、finish=stop，转写质量正常。
			reasoning_effort: 'low',
			messages: [{
				role: 'user',
				content: [
					{type: 'image_url', image_url: {url: dataUrl}},
					{type: 'text', text: PROMPT},
				],
			}],
		}),
	})
	if (!resp.ok) {
		const text = await resp.text()
		if (resp.status === 401 || resp.status === 403) {
			// Key 失效/无权限：清掉让下次重新询问
			localStorage.removeItem(VLM_KEY_STORAGE)
			throw new Error('API Key 无效或已过期，请重新输入')
		}
		// OpenRouter 把原因写在 body 的 error.message 里（额度不足、模型名写错等），
		// 直接吞掉的话用户只会看到"HTTP 402"，无从排查
		const reason = text.match(/"message"\s*:\s*"([^"]*)"/)?.[1]
		throw new Error('识别服务返回错误（HTTP ' + resp.status + '）'
			+ (reason ? '：' + reason : ''))
	}
	// 防御性解析：HTTP 200 不代表有内容。实测模型陷入推理死循环时会返回
	// 一个纯空白的 body，直接 resp.json() 抛的是 SyntaxError，
	// 用户只会看到"Unexpected end of JSON input"，完全对不上真正的原因。
	const raw = await resp.text()
	let data: any = null
	try {
		data = JSON.parse(raw)
	} catch {
		throw new Error('识别服务返回了空响应（模型未能完成识别），请重试或换一张图片')
	}
	const content = (data.choices?.[0]?.message?.content ?? '').trim()
		.replace(/^```[a-z]*\s*/gm, '').replace(/```/g, '').trim()
	if (content === '[FAIL]') throw new Error('图片无法辨认：请确认上传的是清晰的简谱图片')
	if (!content) throw new Error('识别结果为空，请重试或换一张更清晰的图片')
	return ensureSectionSeparators(content)
}

/**
 * 兜底：确保每个「N: + Lc:」谱行是独立段落（段落间 ---）。
 *
 * 为什么不能信 prompt：模型偶尔仍会省略 ---（实测 5 张里 5 张都省了），
 * 而解析器会把没有分隔的多行谱当成同一文章的多个声部/反复段，谱面直接错。
 * 规则：N: 行是段落起点；一个段落里出现第二个 N: 时，在它前面补 ---。
 * 已有 === / --- / P: / Dt: / Lc: 的行原样保留。
 */
export function ensureSectionSeparators(text: string): string {
	const lines = text.split('\n')
	const out: string[] = []
	let sawFirstN = false
	let inSection = false // 当前段落里是否已开过 N: 段
	for(const raw of lines) {
		const line = raw.trim()
		if(line.startsWith('N:')) {
			if(inSection) {
				// 段落里又来一个 N: → 前面补分隔
				out.push('---')
			}
			inSection = true
			sawFirstN = true
			out.push(line)
		} else if(line === '===' || line === '---') {
			inSection = false
			out.push(line)
		} else if(line) {
			out.push(line)
		}
	}
	// 结构保险：没有 === 头但确有谱行时，补出最简骨架
	if(sawFirstN && !lines.some(l => l.trim() === '===')) {
		const firstN = out.findIndex(l => l.startsWith('N:'))
		if(firstN > 0) {
			out.splice(firstN, 0, '===')
		} else {
			out.unshift('===')
		}
	}
	return out.join('\n')
}

function fileToDataUrl(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.onload = () => resolve(reader.result as string)
		reader.onerror = () => reject(new Error('图片读取失败'))
		reader.readAsDataURL(file)
	})
}

/**
 * 把任意可解码的图片统一转成 PNG 的 data URL 再发给模型。
 *
 * 为什么不能直接 `fileToDataUrl` 后改 mime：FileReader 的 mime 取自 File.type，
 * 而 File.type 是按**扩展名**推的——一个内容其实是 GIF、只是后缀写成 .jpg 的文件，
 * 会被打上 image/jpeg 的标签，把 GIF 字节当 JPEG 送出去。上游按标签去解码就废了。
 * （实测就踩到过：vlm-testset/刀剑如梦.jpg，`file` 认出是 GIF 89a，识别直接失败。）
 *
 * 顺带解决两件事：
 * - GIF/WebP 的动画帧只取第一张，谱图本来也只需要静态帧；
 * - 长边压到 1600px，PNG 无损压缩线条图体积很小，避免撞上接口的图片体积上限。
 */
async function toPngDataUrl(file: File): Promise<string> {
	const raw = await fileToDataUrl(file)
	const mime = raw.slice(5, raw.indexOf(';'))
	const img = await new Promise<HTMLImageElement>((resolve, reject) => {
		const el = new Image()
		el.onload = () => resolve(el)
		el.onerror = () => reject(new Error('图片无法解码，请换一张 JPG/PNG 截图'))
		el.src = raw
	})
	const scale = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight))
	const canvas = document.createElement('canvas')
	canvas.width = Math.max(1, Math.round(img.naturalWidth * scale))
	canvas.height = Math.max(1, Math.round(img.naturalHeight * scale))
	const ctx = canvas.getContext('2d')
	if (!ctx) throw new Error('浏览器无法处理该图片')
	// GIF/PNG 可能带透明底，先铺白：透明区在深色主题下会变黑，谱线直接看不见
	ctx.fillStyle = '#ffffff'
	ctx.fillRect(0, 0, canvas.width, canvas.height)
	ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
	const png = canvas.toDataURL('image/png')
	// 兜底：个别浏览器对超大画布会返回 "data:,"，这种情况退回原图
	if (!png.startsWith('data:image/png')) return 'data:' + mime + ';base64,' + stripDataUrl(raw)
	return png
}

function stripDataUrl(dataUrl: string): string {
	return dataUrl.slice(dataUrl.indexOf(',') + 1)
}

/** 从识别出的 NMN 源码里提取 Dt: 标题，没有就回落文件名 */
export function extractTitleFromNmn(content: string, fallback: string): string {
	const m = content.match(/^Dt:\s*(.+)$/m)
	const title = m ? m[1].trim() : ''
	return title && title !== '未知' ? title : fallback
}
